import { z } from 'zod';

/**
 * Google Ads offline-conversion events — the record of which paying churches
 * are waiting to be uploaded, and how much time is left to do it.
 *
 * ## Why this domain exists
 *
 * The exporter (#335) turns `Church.attribution.gclid` + first settled Stripe
 * payment into a CSV a human uploads by hand. Until 2026-08-28 there was no
 * Google Ads API access for CID 424-544-2044, so nothing in this system could
 * complete the loop on its own — a person had to act, which made the sweep's
 * only job NOTICING. With Basic Access approved the sweep now uploads directly
 * (`ads.offline_conversions_uploaded` below); the manual CSV is the fallback
 * when the upload path is unconfigured or failing. Silence is still the
 * failure mode:
 *
 *   - A payment that is never uploaded ages past the conversion action's 90-day
 *     click-through window and is then REJECTED. The money was real, the click
 *     was real, and Google simply never learns the two are connected.
 *   - Nothing complains when that happens. The row stops appearing in the
 *     export the day it expires, which reads exactly like "there was nothing to
 *     upload" — the same wrong-but-plausible quiet as the WA checkout incident
 *     behind ADR 0002.
 *
 * The interim mitigation was a monthly scheduled task on Rachel's desktop,
 * which only fires while the app is open. This domain is what replaces it.
 *
 * ## What it has to answer
 *
 *   1. "Did the sweep actually run, and what did it see?"
 *      → `ads.offline_conversions_swept`, emitted EVERY run including the runs
 *        that found nothing. A run that found zero and a run that never
 *        happened must not look the same.
 *   2. "Is there anything for me to upload right now?"
 *      → `ads.offline_conversion_pending`, one per uploadable row. It names
 *        names, so the alert is actionable without a database.
 *   3. "Is anything about to expire?"
 *      → `ads.offline_conversion_expiring`, the escalation. A row at 60 days is
 *        routine; the identical row at 5 days is about to be lost for good.
 *
 * Failures reuse `sweep.run_failed` with `sweep: 'offline_conversion'` — a
 * crashing sweep is the same question whichever sweep crashed, and answering it
 * in one place is worth more than a bespoke event.
 *
 * ## Expected state at first deploy
 *
 * The verified prod dry run on 2026-08-18 found 14 churches with attribution
 * and ZERO uploadable (5 gclid-but-no-Stripe-customer, 2 customer-but-no-gclid,
 * 7 neither). Attribution capture is forward-only from #329, so a long run of
 * `uploadable: 0` is the CORRECT answer, not a broken sweep. That is precisely
 * why event 1 above is unconditional: it is the only thing distinguishing
 * "correctly nothing" from "silently dead".
 */

/** Why a candidate church did not become an upload row. Mirrors the reasons
 *  `selectConversionRows` produces, collapsed to a queryable enum. */
export const OFFLINE_CONVERSION_SKIP_REASONS = [
  /** Internal / demo / free-forever church — never trains the bidder. */
  'excluded',
  /** Has attribution, but no `gclid` in it — arrived from a non-Google source. */
  'no_gclid',
  /** Has a gclid, but no settled Stripe payment yet. The common case. */
  'no_payment',
  /** Money settled but carries no usable timestamp; Google requires one. */
  'undateable_payment',
  /** Payment is already older than the click-through window — unrecoverable. */
  'past_window',
] as const;

export type OfflineConversionSkipReason =
  (typeof OFFLINE_CONVERSION_SKIP_REASONS)[number];

export const adsSchemas = {
  /**
   * One per sweep run, ALWAYS — including runs that find nothing uploadable.
   *
   * This is the heartbeat that makes the rest trustworthy. Without it, the
   * absence of `ads.offline_conversion_pending` is ambiguous between "no church
   * with a gclid has paid" (true today) and "the sweep has been dead for three
   * weeks", and those two need completely different responses.
   */
  'ads.offline_conversions_swept': z.object({
    /** Churches carrying any attribution at all — the pool that was examined. */
    candidates: z.number().int().nonnegative(),
    /** Uploadable rows found. The number the alert is about. */
    uploadable: z.number().int().nonnegative(),
    /** Of those, how many are inside the escalation threshold. */
    expiringSoon: z.number().int().nonnegative(),
    /**
     * Rows already past the window on this run — money that can no longer be
     * uploaded at all. Non-zero here means the loop failed: a real conversion
     * was earned and permanently lost.
     */
    expired: z.number().int().nonnegative(),
    /** Skip reasons tallied, so the shape of the pool is visible over time. */
    skipped: z.record(z.enum(OFFLINE_CONVERSION_SKIP_REASONS), z.number()),
    /** The click-through window the run measured against, in days. */
    windowDays: z.number().int().positive(),
  }),

  /**
   * One per uploadable row. Names the church and the headroom, so the Slack /
   * Rollbar alert is actionable without opening a database or rerunning the
   * exporter.
   *
   * No gclid in the payload on purpose — it is a click identifier tied to a
   * real person's ad click, and it has no business sitting in analytics
   * storage. The church name is enough to act on; the exporter regenerates the
   * gclid when the file is actually built.
   */
  'ads.offline_conversion_pending': z.object({
    churchName: z.string(),
    /** Whole days before this payment falls outside the click-through window. */
    daysRemaining: z.number().int(),
    /** Minor units of the first settled payment, when it carried a value. */
    amountMinor: z.number().int().nonnegative().nullable(),
    currency: z.string().nullable(),
  }),

  /**
   * The escalation: an uploadable row inside the warning threshold.
   *
   * Deliberately a separate event rather than a flag on the one above, so a
   * monitor can alert on THIS existing at all without having to filter — the
   * distinction between "someone should upload soon" and "upload this week or
   * lose the conversion" is the whole point of the sweep.
   */
  'ads.offline_conversion_expiring': z.object({
    churchName: z.string(),
    daysRemaining: z.number().int(),
    /** The threshold that classified it, so a later change to the threshold is
     *  visible in the history rather than silently reinterpreting old events. */
    thresholdDays: z.number().int().positive(),
  }),

  /**
   * One per run in which the sweep attempted an API upload — including runs
   * where the attempt fell over entirely (`failureReason` set, `accepted` 0).
   * Same rule as the heartbeat: an upload path that only reports its successes
   * is indistinguishable from one that stopped being tried.
   *
   * "Accepted", not "credited": the Data Manager API takes the batch and
   * matches gclids to clicks asynchronously, so a row Google ultimately cannot
   * match never errors back here. Whether conversions actually land is read in
   * the Ads UI / campaign metrics — the same place the CSV uploads were
   * verified. Idempotency across runs comes from the deterministic
   * `transactionId` (gclid + charge time), so re-sending the whole window
   * every day cannot double-count.
   */
  'ads.offline_conversions_uploaded': z.object({
    /** Rows sent to Google on this run. */
    attempted: z.number().int().nonnegative(),
    /** Rows Google accepted into processing — all of them, on a 2xx. */
    accepted: z.number().int().nonnegative(),
    /** Row-level field warnings in the response — schema drift made visible. */
    warnings: z.number().int().nonnegative(),
    /** True for a rehearsal: Google validated the batch but recorded nothing. */
    validateOnly: z.boolean(),
    /** Set when the attempt died (auth, scope, lookup, transport) before
     *  Google saw the rows. Message/status only — never a credential. */
    failureReason: z.string().nullable(),
  }),

  /**
   * A row the API upload could not submit — still waiting on a human, exactly
   * as if the upload path did not exist. Fires alongside the manual-path
   * Rollbar alert so the fallback is visible in the event stream too.
   */
  'ads.offline_conversion_upload_failed': z.object({
    churchName: z.string(),
    /** Why the row never reached Google — today always `transport` (the whole
     *  attempt failed; per-row rejections don't exist on the async API). */
    reason: z.string(),
  }),
} as const;
